import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Checkbox,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import { formatShortDate, timeAgo } from '../../utils/dateUtils';
import { HEADER_SX, CELL_SX, ROW_SX, TABLE_CONTAINER_SX } from './DataTable/tableTheme';
import MiniConversationCard from './MiniConversationCard';
import {
  TICKET_STATUSES, TICKET_STATUS_VALUES, TERMINAL_STATUSES,
} from '../../constants/ticketStatuses';

export interface Ticket {
  id: number | string;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  assigned_to?: number | string;
  assigned_name?: string;
  due_date?: string;
  client_name?: string;
  client_card_id?: string;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  closed_at?: string;
  thread_id?: number;
  // Extended for MyTicketsView-style metric tickets
  url?: string;
  metric_name?: string;
  stage?: string;
  resolved_date?: string;
  week_date?: string;
  variance?: { days: number; label: string; color: string } | null;
}

// Filter pill set. 'all' is a UI sentinel meaning "no status filter"; every
// other entry is a canonical TicketStatus value, so this filter set narrows
// to a meaningful subset of the W2 canonical enum. Callers can override via
// the `statusOptions` prop.
const DEFAULT_STATUS_OPTIONS: string[] = [...TICKET_STATUS_VALUES, 'all'];

const PRIORITY_COLORS: Record<string, string> = {
  high: 'var(--status-off-track)',
  medium: 'var(--status-warning)',
  low: 'var(--status-on-track)',
  urgent: 'var(--status-off-track)',
};

function getPriorityColor(priority: string | undefined): string {
  if (!priority) return 'var(--status-neutral)';
  return PRIORITY_COLORS[priority.toLowerCase()] || 'var(--status-neutral)';
}

export interface TicketTableProps {
  tickets: Ticket[];
  loading: boolean;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onStatusChange?: (ticketId: number | string, newStatus: string) => void;
  onClose?: (ticketId: number | string) => void;
  onNavigateToThread?: (threadId: number) => void;
  onNavigateToClient?: (clientCardId: string) => void;
  statusOptions?: string[];
  statusLabels?: Record<string, string>;
  showClientColumn?: boolean;
  showAssignedColumn?: boolean;
  showPriorityColumn?: boolean;
  showDueColumn?: boolean;
  showMetricColumn?: boolean;
  showStageColumn?: boolean;
  showResolvedColumn?: boolean;
  showVarianceColumn?: boolean;
  showWeekColumn?: boolean;
  showIdColumn?: boolean;
  showActionsColumn?: boolean;
  showCheckboxColumn?: boolean;
  selectedIds?: Set<number | string>;
  onSelectionChange?: (selectedIds: Set<number | string>) => void;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  sortableColumns?: string[];
  hideStatusFilter?: boolean;
  emptyMessage?: string;
  pagination?: {
    page: number;
    total: number;
    limit: number;
    onPageChange: (page: number) => void;
  };
}

export default function TicketTable({
  tickets,
  loading,
  statusFilter,
  onStatusFilterChange,
  onStatusChange,
  onClose,
  onNavigateToThread,
  onNavigateToClient,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  statusLabels,
  showClientColumn = true,
  showAssignedColumn = true,
  showPriorityColumn = true,
  showDueColumn = true,
  showMetricColumn = false,
  showStageColumn = false,
  showResolvedColumn = false,
  showVarianceColumn = false,
  showWeekColumn = false,
  showIdColumn = true,
  showActionsColumn = true,
  showCheckboxColumn = false,
  selectedIds,
  onSelectionChange,
  sortField,
  sortDir = 'asc',
  onSort,
  sortableColumns,
  hideStatusFilter = false,
  emptyMessage = 'No tickets found',
  pagination,
}: TicketTableProps) {
  const theme = useTheme();
  const [conversationThreadId, setConversationThreadId] = useState<number | null>(null);

  const formatStatusLabel = (value: string) => {
    if (statusLabels?.[value]) return statusLabels[value];
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const statusToggleOptions = statusOptions.map((opt) => ({
    value: opt,
    label: formatStatusLabel(opt),
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Skeleton variant="rounded" width={80} height={32} />
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={100} height={32} />
        </Box>
        <Box sx={TABLE_CONTAINER_SX(theme)}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {showIdColumn && <TableCell sx={HEADER_SX(theme)}><Skeleton width={40} /></TableCell>}
                <TableCell sx={HEADER_SX(theme)}><Skeleton width={120} /></TableCell>
                {showClientColumn && <TableCell sx={HEADER_SX(theme)}><Skeleton width={80} /></TableCell>}
                {showAssignedColumn && <TableCell sx={HEADER_SX(theme)}><Skeleton width={80} /></TableCell>}
                {showPriorityColumn && <TableCell sx={HEADER_SX(theme)}><Skeleton width={60} /></TableCell>}
                {showDueColumn && <TableCell sx={HEADER_SX(theme)}><Skeleton width={70} /></TableCell>}
                <TableCell sx={HEADER_SX(theme)}><Skeleton width={70} /></TableCell>
                {showActionsColumn && <TableCell sx={HEADER_SX(theme)}><Skeleton width={80} /></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {showIdColumn && <TableCell sx={CELL_SX(theme)}><Skeleton width={30} /></TableCell>}
                  <TableCell sx={CELL_SX(theme)}><Skeleton width={150} /></TableCell>
                  {showClientColumn && <TableCell sx={CELL_SX(theme)}><Skeleton width={80} /></TableCell>}
                  {showAssignedColumn && <TableCell sx={CELL_SX(theme)}><Skeleton width={80} /></TableCell>}
                  {showPriorityColumn && <TableCell sx={CELL_SX(theme)}><Skeleton width={60} /></TableCell>}
                  {showDueColumn && <TableCell sx={CELL_SX(theme)}><Skeleton width={70} /></TableCell>}
                  <TableCell sx={CELL_SX(theme)}><Skeleton width={70} /></TableCell>
                  {showActionsColumn && <TableCell sx={CELL_SX(theme)}><Skeleton width={80} /></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  }

  const hasActions = showActionsColumn && (onStatusChange || onClose);
  const hasCheckbox = showCheckboxColumn && selectedIds !== undefined && onSelectionChange;

  const sortable = (label: string, field: string, sx?: object) => {
    const isSortable = sortableColumns?.includes(field) && onSort;
    return (
      <TableCell sx={{ ...HEADER_SX(theme), ...sx }}>
        {isSortable ? (
          <TableSortLabel
            active={sortField === field}
            direction={sortField === field ? sortDir : 'asc'}
            onClick={() => onSort(field)}
            sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
          >
            {label}
          </TableSortLabel>
        ) : (
          label
        )}
      </TableCell>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!hideStatusFilter && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, v) => v != null && onStatusFilterChange(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '12.5px' } }}
          >
            {statusToggleOptions.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={TABLE_CONTAINER_SX(theme)}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {hasCheckbox && (
                <TableCell padding="checkbox" sx={{ ...HEADER_SX(theme), width: 40 }}>
                  <Checkbox
                    size="small"
                    checked={tickets.length > 0 && selectedIds!.size === tickets.length}
                    indeterminate={selectedIds!.size > 0 && selectedIds!.size < tickets.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onSelectionChange!(new Set(tickets.map((t) => t.id)));
                      } else {
                        onSelectionChange!(new Set());
                      }
                    }}
                    sx={{ p: 0.25 }}
                  />
                </TableCell>
              )}
              {showIdColumn && <TableCell sx={{ ...HEADER_SX(theme), width: 50 }}>ID</TableCell>}
              {sortableColumns?.includes('title') && onSort ? sortable('Title', 'title') : <TableCell sx={HEADER_SX(theme)}>Title</TableCell>}
              {showClientColumn && (sortableColumns?.includes('client') && onSort ? sortable('Client', 'client', { width: 150 }) : <TableCell sx={{ ...HEADER_SX(theme), width: 150 }}>Client</TableCell>)}
              {showAssignedColumn && <TableCell sx={{ ...HEADER_SX(theme), width: 120 }}>Assigned</TableCell>}
              {showMetricColumn && (sortableColumns?.includes('metric') && onSort ? sortable('Metric', 'metric', { width: 110 }) : <TableCell sx={{ ...HEADER_SX(theme), width: 110 }}>Metric</TableCell>)}
              {showStageColumn && (sortableColumns?.includes('stage') && onSort ? sortable('Stage', 'stage', { width: 120 }) : <TableCell sx={{ ...HEADER_SX(theme), width: 120 }}>Stage</TableCell>)}
              {showPriorityColumn && <TableCell sx={{ ...HEADER_SX(theme), width: 90 }}>Priority</TableCell>}
              {showDueColumn && (sortableColumns?.includes('due') && onSort ? sortable('Due', 'due', { width: 90, textAlign: 'center' }) : <TableCell sx={{ ...HEADER_SX(theme), width: 90, textAlign: 'center' }}>Due</TableCell>)}
              {showResolvedColumn && (sortableColumns?.includes('resolved') && onSort ? sortable('Resolved', 'resolved', { width: 90, textAlign: 'center' }) : <TableCell sx={{ ...HEADER_SX(theme), width: 90, textAlign: 'center' }}>Resolved</TableCell>)}
              {sortableColumns?.includes('status') && onSort ? sortable('Status', 'status', { width: 90, textAlign: 'center' }) : <TableCell sx={{ ...HEADER_SX(theme), width: 90, textAlign: 'center' }}>Status</TableCell>}
              {showVarianceColumn && (sortableColumns?.includes('variance') && onSort ? sortable('Variance', 'variance', { width: 100, textAlign: 'center' }) : <TableCell sx={{ ...HEADER_SX(theme), width: 100, textAlign: 'center' }}>Variance</TableCell>)}
              {showWeekColumn && (sortableColumns?.includes('week') && onSort ? sortable('Week', 'week', { width: 90, textAlign: 'center' }) : <TableCell sx={{ ...HEADER_SX(theme), width: 90, textAlign: 'center' }}>Week</TableCell>)}
              {hasActions && <TableCell sx={{ ...HEADER_SX(theme), width: 120 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.length === 0 ? (
              <TableRow sx={ROW_SX(theme)}>
                <TableCell
                  colSpan={
                    2 +
                    (hasCheckbox ? 1 : 0) +
                    (showIdColumn ? 1 : 0) +
                    (showClientColumn ? 1 : 0) +
                    (showAssignedColumn ? 1 : 0) +
                    (showMetricColumn ? 1 : 0) +
                    (showStageColumn ? 1 : 0) +
                    (showPriorityColumn ? 1 : 0) +
                    (showDueColumn ? 1 : 0) +
                    (showResolvedColumn ? 1 : 0) +
                    (showVarianceColumn ? 1 : 0) +
                    (showWeekColumn ? 1 : 0) +
                    (hasActions ? 1 : 0)
                  }
                  align="center"
                  sx={{ ...CELL_SX(theme), py: 4 }}
                >
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket, idx) => {
                const dueDate = ticket.due_date;
                const isOverdue = dueDate ? new Date(dueDate).getTime() < Date.now() : false;
                const isNearDue = dueDate && !isOverdue && (new Date(dueDate).getTime() - Date.now()) < 3 * 86400000;

                const isSelected = hasCheckbox && selectedIds!.has(ticket.id);
                return (
                  <TableRow
                    key={ticket.id ?? idx}
                    selected={isSelected}
                    sx={{
                      ...ROW_SX(theme),
                      borderLeft:
                        ticket.status === 'positive'
                          ? '3px solid rgba(76,175,80,0.5)'
                          : ticket.status === 'negative'
                          ? '3px solid rgba(207,102,121,0.5)'
                          : undefined,
                    }}
                  >
                    {hasCheckbox && (
                      <TableCell padding="checkbox" sx={CELL_SX(theme)}>
                        <Checkbox
                          size="small"
                          checked={!!isSelected}
                          onChange={() => {
                            const next = new Set(selectedIds!);
                            if (next.has(ticket.id)) next.delete(ticket.id);
                            else next.add(ticket.id);
                            onSelectionChange!(next);
                          }}
                          sx={{ p: 0.25 }}
                        />
                      </TableCell>
                    )}
                    {showIdColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), fontSize: '12.5px', color: 'text.secondary' }}>
                        #{ticket.id}
                      </TableCell>
                    )}
                    <TableCell sx={{ ...CELL_SX(theme), maxWidth: 280 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.url ? (
                            <Link
                              href={ticket.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontWeight: 500,
                                fontSize: '12.5px',
                                color: 'primary.light',
                                '&:hover': { color: 'primary.main' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.title}</Box>
                              <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.35, flexShrink: 0 }} />
                            </Link>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ticket.title}
                              </Typography>
                              {ticket.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {ticket.description.substring(0, 80)}
                                  {ticket.description.length > 80 ? '…' : ''}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                        {ticket.thread_id && (
                          <Tooltip title="View email conversation">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onNavigateToThread) {
                                  onNavigateToThread(ticket.thread_id!);
                                } else {
                                  setConversationThreadId(ticket.thread_id!);
                                }
                              }}
                              sx={{
                                flexShrink: 0,
                                color: 'primary.main',
                                opacity: 0.7,
                                '&:hover': { opacity: 1, bgcolor: 'primary.main', color: 'primary.contrastText' },
                                width: 26,
                                height: 26,
                              }}
                            >
                              <EmailIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {showClientColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ticket.client_card_id && onNavigateToClient ? (
                          <Link
                            component="button"
                            variant="body2"
                            sx={{ fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                            onClick={() => onNavigateToClient(ticket.client_card_id!)}
                          >
                            {ticket.client_name || '—'}
                          </Link>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: ticket.client_name ? 'text.primary' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ticket.client_name || '—'}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {showAssignedColumn && (
                      <TableCell sx={CELL_SX(theme)}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                          {ticket.assigned_name || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {showMetricColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '12.5px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.metric_name || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {showStageColumn && (
                      <TableCell sx={CELL_SX(theme)}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: ticket.stage ? 'text.primary' : 'text.secondary' }}>
                          {ticket.stage || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {showPriorityColumn && (
                      <TableCell sx={CELL_SX(theme)}>
                        {ticket.priority ? (
                          <Chip
                            label={ticket.priority}
                            size="small"
                            variant="outlined"
                            sx={{
                              textTransform: 'capitalize',
                              fontSize: '11px',
                              height: 22,
                              borderColor: getPriorityColor(ticket.priority),
                              color: getPriorityColor(ticket.priority),
                            }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>—</Typography>
                        )}
                      </TableCell>
                    )}
                    {showDueColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), textAlign: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {formatShortDate(dueDate)}
                          </Typography>
                          {dueDate && (() => {
                            const rel = timeAgo(dueDate);
                            return rel ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10.5px',
                                  color: isOverdue ? 'error.main' : isNearDue ? 'warning.main' : 'text.disabled',
                                  display: 'block',
                                  lineHeight: 1,
                                }}
                              >
                                {rel}
                              </Typography>
                            ) : null;
                          })()}
                        </Box>
                      </TableCell>
                    )}
                    {showResolvedColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), textAlign: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {formatShortDate(ticket.resolved_date)}
                          </Typography>
                          {ticket.resolved_date && (() => {
                            const rel = timeAgo(ticket.resolved_date);
                            return rel ? (
                              <Typography variant="caption" sx={{ fontSize: '10.5px', color: 'text.disabled', display: 'block', lineHeight: 1 }}>
                                {rel}
                              </Typography>
                            ) : null;
                          })()}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell sx={{ ...CELL_SX(theme), textAlign: 'center' }}>
                      {statusLabels?.[ticket.status] ? (
                        <Chip
                          label={statusLabels[ticket.status]}
                          size="small"
                          sx={{
                            fontSize: '10.5px',
                            height: 20,
                            fontWeight: 600,
                            bgcolor:
                              ticket.status === 'positive'
                                ? 'rgba(76,175,80,0.15)'
                                : ticket.status === 'negative'
                                ? 'rgba(207,102,121,0.15)'
                                : 'rgba(255,255,255,0.06)',
                            color:
                              ticket.status === 'positive'
                                ? 'var(--status-on-track)'
                                : ticket.status === 'negative'
                                ? 'var(--status-off-track)'
                                : 'text.secondary',
                          }}
                        />
                      ) : (
                        <Chip
                          label={formatStatusLabel(ticket.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize', fontSize: '11px' }}
                        />
                      )}
                    </TableCell>
                    {showVarianceColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), textAlign: 'center' }}>
                        {ticket.variance ? (
                          <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: ticket.variance.color }}>
                            {ticket.variance.label}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>—</Typography>
                        )}
                      </TableCell>
                    )}
                    {showWeekColumn && (
                      <TableCell sx={{ ...CELL_SX(theme), textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                          {formatShortDate(ticket.week_date)}
                        </Typography>
                      </TableCell>
                    )}
                    {hasActions && (
                      <TableCell sx={CELL_SX(theme)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {onStatusChange && (
                            <Select
                              size="small"
                              value={ticket.status}
                              onChange={(e) => onStatusChange(ticket.id, e.target.value)}
                              sx={{ fontSize: '12.5px', minWidth: 110 }}
                            >
                              {TICKET_STATUSES.map(s => (
                                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                              ))}
                            </Select>
                          )}
                          {onClose && !(TERMINAL_STATUSES as readonly string[]).includes(ticket.status) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ textTransform: 'none', fontSize: '11px' }}
                              onClick={() => onClose(ticket.id)}
                            >
                              Close
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {pagination && pagination.total > pagination.limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
          <Button
            size="small"
            disabled={pagination.page <= 1}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption" sx={{ alignSelf: 'center' }}>
            Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
          </Typography>
          <Button
            size="small"
            disabled={pagination.page * pagination.limit >= pagination.total}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </Box>
      )}

      <MiniConversationCard
        open={conversationThreadId !== null}
        onClose={() => setConversationThreadId(null)}
        threadId={conversationThreadId ?? 0}
      />
    </Box>
  );
}
